import { Outlet, createFileRoute } from "@tanstack/react-router";

import { useQuery } from "@apollo/client/react";
import type { GetShowThumbnailsQuery } from "@/codegen/graphql/generated/graphql";
import FadeInImage from "@/components/FadeInImage";
import { GET_SHOW_THUMBNAILS } from "@/codegen/graphql/queries/GetShowsThumbnails.query";
import { cn } from "@/lib/utils";
import AuthTemplate from "@/components/templates/AuthTemplate";
import useFormat from "@/hooks/useFormat";

export const Route = createFileRoute("/_external")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data } = useQuery<GetShowThumbnailsQuery>(GET_SHOW_THUMBNAILS);
  const { getResizedThumbnailUrl } = useFormat();

  const showsFormatted:
    | Array<Array<{ id: string; thumbnail: string }>>
    | undefined = data?.shows
    .reduce<Array<Array<{ id: string; thumbnail: string }>>>((acc, show) => {
      if (acc.length === 0 || acc[acc.length - 1].length === 6) {
        acc.push([
          {
            id: show.id,
            thumbnail: getResizedThumbnailUrl(
              show.displayThumbnail.original,
              "240",
            ),
          },
        ]);
        return acc;
      }
      acc[acc.length - 1].push({
        id: show.id,
        thumbnail: getResizedThumbnailUrl(
          show.displayThumbnail.original,
          "240",
        ),
      });
      return [...acc];
    }, [])
    .filter((_, index, arr) => index !== arr.length - 1);

  return (
    <AuthTemplate>
      <Outlet />
      <div className="absolute -top-10 bottom-0 left-1/2 -z-10 flex max-h-[max(50%,700px)] w-full max-w-5xl -translate-x-1/2 justify-center gap-2 overflow-hidden">
        <div className="bg-neutral-70/50 absolute -top-20 h-[calc(100%+5rem)] w-[130%] shadow-[inset_0_0_100px_100px_#13161D] sm:top-0 sm:h-full sm:w-full" />
        {showsFormatted?.map((row, index) => (
          <div key={index} className="grid shrink-0 gap-2 odd:mt-16 even:mb-16">
            {row.map((show) => (
              <FadeInImage
                key={show.id}
                className={cn("aspect-3/4 h-34 rounded object-cover")}
                src={show.thumbnail}
                alt={show.id}
              />
            ))}
          </div>
        ))}
      </div>
    </AuthTemplate>
  );
}
